import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import useCioAutosuggest from '~/searchCio/hooks/useCioAutosuggest';
import {
  constructorTextStandard,
  constructorTextBold,
  constructorTextHighlight,
} from '~/searchCio/utils/styles';
import { productDetailPath } from '~/shared/constants/product';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { Picture, mobile, Heading } from '~/techstyle-shared/react-components';
import { buildURL } from '~/techstyle-shared/redux-core';

const breakpoints = [{ minWidth: 0, pictureWidth: 168 }];

export const AutoSuggestSection = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 24px 1fr;
  grid-column-gap: 0px;
  grid-row-gap: 8px;
`;

export const AutoSuggestSectionTitle = styled(Heading)`
  ${constructorTextBold}
  gap: 16px;
  text-transform: uppercase;
  padding: 0;
  margin: 0;
  cursor: default;
  font-size: 16px;
  justify-content: flex-start;
  align-self: center;
`;

export const AutoSuggestList = styled.ul`
  ${({ theme }) => theme.lists.bare};
`;

export const AutoSuggestListItem = styled.li`
  ${constructorTextStandard}
  ${constructorTextHighlight}
  cursor: pointer;
  display: flex;
  padding: 8px 0px;

  &:before {
    left: -6px;
  }
`;

export const CioOverlayProductsSectionWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 24px 1fr;
  grid-column-gap: 0px;
  grid-row-gap: 16px;
`;

export const CioOverlayProductsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-column-gap: 8px;

  ${mobile`
      grid-template-columns: 1fr 1fr;
      grid-auto-flow: row dense;
      grid-column-gap: 8px;
      grid-row-gap: 16px;
    `};
`;

export const CioOverlayProductsTitle = styled.div`
  ${constructorTextBold}
  text-transform: uppercase;
  font-size: 16px;
`;

export const CioOverlayNoResultsFoundTitle = styled.div`
  ${constructorTextStandard}
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.searchCioOverlayNoResultsFoundTitle};
  padding-bottom: 16px;
  font-size: 16px;
`;

const CioOverlayProductTitle = styled.div`
  padding: 0 2px;
  ${constructorTextStandard}
`;

const ProductImageWrapper = styled.div`
  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 100;
    }
  }
  animation: fadeIn 800ms;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin: 0px;
  width: 100%;
  ${({ theme }) => theme.gridCellStyles.upsellCarousel.productImageWrapper}
`;

const CioOverlayProductWrapper = styled.div`
  cursor: pointer;
  margin: 0;
  padding: 0;
  vertical-align: baseline;
`;

/**
 * @function CioOverlayProduct
 * @description Intended to be used for Trending Products and Recommended
 * Products in the constructor search overlay.
 * Requires the use of downshift and constructor search history context to
 * provide arrow key navigation through search overlay.
 * @param {Object} product the product to display and link to
 * @returns {Object} A product component that displays the product image,
 * product name, and links to the product's PDP page.
 */
export const CioOverlayProduct = ({ dataTags = {}, product, psrc }) => {
  const secondarySite = useSecondarySite();
  const routePrefix = secondarySite ? `/${secondarySite}` : '';

  const href = buildURL(
    `${routePrefix}/${productDetailPath}/${product.permalink}`,
    {
      psrc,
    }
  );

  const { handleProductSelect } = useCioAutosuggest();
  return (
    <CioOverlayProductWrapper
      role="link"
      tabIndex={0}
      onClick={() => {
        handleProductSelect({ href, product });
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleProductSelect({ href, product });
        }
      }}
      data-cnstrc-item-name={product.label}
      data-cnstrc-item-id={product.id}
      {...dataTags}
    >
      <ProductImageWrapper>
        <Picture
          alt={product.label}
          breakpoints={breakpoints}
          sources={[{ images: [{ width: 168, url: product.image_url }] }]}
        />
      </ProductImageWrapper>
      <CioOverlayProductTitle>{product.label}</CioOverlayProductTitle>
    </CioOverlayProductWrapper>
  );
};
CioOverlayProduct.propTypes = {
  dataTags: PropTypes.object,
  product: PropTypes.object,
  psrc: PropTypes.string,
};

const StandardText = styled.span`
  ${constructorTextStandard};
  white-space: pre-wrap;
  display: inline;
`;

const BoldText = styled.span`
  ${constructorTextBold};
  white-space: pre-wrap;
  display: inline;
  font-weight: bold;
`;

const TextWrapper = styled.div`
  display: inline;
  word-break: break-all;
`;

/**
 * Bold any substring in mainString that matches subString.
 *
 * @param {string} mainString string to be displayed. may or may not contain
 * subString
 * @param {string} subString any instance of this substring in mainString will
 * be bolded
 * @returns {JSX.Element} the stylized mainString
 */
export const TextWithBoldSubString = ({ mainString, subString }) => {
  // split mainString into sections separated by subString
  const stringArray = mainString.split(subString);

  // no match found, return whole mainString
  if (stringArray.length === 1) {
    return (
      <TextWrapper>
        <StandardText>{mainString}</StandardText>
      </TextWrapper>
    );
  }

  // else return a bolded subSring between every element in stringArray
  return (
    <TextWrapper>
      {stringArray.map((stringElem, index) => (
        <React.Fragment key={index}>
          <StandardText>{stringElem}</StandardText>
          {index !== stringArray.length - 1 && <BoldText>{subString}</BoldText>}
        </React.Fragment>
      ))}
    </TextWrapper>
  );
};
TextWithBoldSubString.propTypes = {
  mainString: PropTypes.string,
  subString: PropTypes.string,
};
